import { motion } from 'framer-motion';

export function SeekersSection() {
  return (
    <section className="relative py-16 lg:py-32 bg-[#0a0a0a] overflow-hidden">

      {/* ── Fluid Ambient Glow ── */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 700,
          height: 700,
          background: 'radial-gradient(circle, rgba(0,217,255,0.18) 0%, transparent 70%)',
          filter: 'blur(80px)',
          top: '-10%',
          right: '-15%',
        }}
        animate={{
          x: [0, 60, -40, 0],
          y: [0, -50, 30, 0],
          scale: [1, 1.15, 0.9, 1],
        }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 500,
          height: 500,
          background: 'radial-gradient(circle, rgba(0,217,255,0.10) 0%, transparent 70%)',
          filter: 'blur(100px)',
          bottom: '5%',
          left: '-10%',
        }}
        animate={{
          x: [0, -50, 30, 0],
          y: [0, 40, -30, 0],
          scale: [1, 0.85, 1.1, 1],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
      />
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 400,
          height: 400,
          background: 'radial-gradient(circle, rgba(0,180,255,0.08) 0%, transparent 70%)',
          filter: 'blur(120px)',
          top: '40%',
          left: '30%',
        }}
        animate={{
          x: [0, 80, -60, 0],
          y: [0, -60, 40, 0],
          scale: [1, 1.2, 0.8, 1],
        }}
        transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut', delay: 6 }}
      />

      <div className="container mx-auto px-4 sm:px-8 relative z-10">
        {/* Section Title */}
        <div className="mb-10 lg:mb-20 relative">
          <div
            className="absolute -right-2 lg:-right-4 top-0 w-1 h-20 lg:h-32 bg-neon-blue"
            style={{
              boxShadow: '0 0 20px rgba(0, 217, 255, 0.9), 0 0 60px rgba(0, 217, 255, 0.3), 0 0 100px rgba(0, 217, 255, 0.1)',
            }}
          />

          <div className="text-right">
            <h2
              className="font-display text-4xl lg:text-7xl mb-3 lg:mb-6 tracking-wider"
              style={{
                color: '#00d9ff',
                textShadow: '0 0 20px rgba(0,217,255,0.9), 0 0 40px rgba(0,217,255,0.5), 0 0 80px rgba(0,217,255,0.2)',
              }}
            >
              SEEKERS
              <span
                className="block text-2xl lg:text-5xl mt-1 lg:mt-2"
                style={{
                  color: '#00d9ff',
                  textShadow: '0 0 20px rgba(0,217,255,0.8), 0 0 40px rgba(0,217,255,0.4), 0 0 80px rgba(0,217,255,0.15)',
                }}
              >
                探索者たち
              </span>
            </h2>

            {/* Catchcopy */}
            <p
              className="text-xl lg:text-3xl font-bold mb-4 lg:mb-8 tracking-wide"
              style={{ color: '#ffffff', textShadow: 'none' }}
            >
              「推薦」よりも、「偶然」を。
            </p>

            <p className="text-gray-400 text-sm lg:text-xl max-w-3xl ml-auto leading-relaxed">
              スマホを閉じ、自らの衝動に従って街へ踏み出す探索者。各地に潜む
              <strong
                style={{ color: '#00d9ff', textShadow: '0 0 16px rgba(0,217,255,0.8), 0 0 32px rgba(0,217,255,0.3)' }}
              >
                『体温のある音』
              </strong>
              を掘り起こし、誰かの表現と衝突する瞬間を愛す。
            </p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-12">
          {/* Feature 1 */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-br from-neon-pink/10 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500" />
            <div className="relative bg-black/30 backdrop-blur-sm rounded-2xl p-6 lg:p-10 border border-white/5 hover:border-neon-pink/40 transition-all duration-500 hover:shadow-[0_0_40px_rgba(255,0,110,0.12)]">
              <div className="flex lg:block items-center gap-4">
                <div
                  className="w-12 h-12 lg:w-16 lg:h-16 lg:mb-8 rounded-xl lg:rounded-2xl bg-black border-2 border-neon-pink flex items-center justify-center flex-shrink-0"
                  style={{
                    boxShadow: '0 0 30px rgba(255, 0, 110, 0.5), 0 0 60px rgba(255, 0, 110, 0.15)',
                  }}
                >
                  <i className="ri-compass-3-line text-xl lg:text-3xl text-neon-pink"></i>
                </div>
                <div className="flex-1 lg:flex-none">
                  <h3 className="text-white text-lg lg:text-2xl font-bold mb-1 lg:mb-4">
                    偶然の発見
                  </h3>
                  <div
                    className="w-2/5 h-0.5 bg-neon-pink mb-3 lg:mb-6 hidden lg:block"
                    style={{
                      boxShadow: '0 0 10px rgba(255,0,110,0.8), 0 0 20px rgba(255,0,110,0.3)',
                    }}
                  />
                  <p className="text-gray-400 text-xs lg:text-base leading-relaxed">
                    街中の物体にスマホをタップするだけ。予測不可能な音楽との出会いが、あなたの日常を変える。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2 */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-br from-neon-green/10 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500" />
            <div className="relative bg-black/30 backdrop-blur-sm rounded-2xl p-6 lg:p-10 border border-white/5 hover:border-neon-green/40 transition-all duration-500 hover:shadow-[0_0_40px_rgba(0,255,65,0.12)]">
              <div className="flex lg:block items-center gap-4">
                <div
                  className="w-12 h-12 lg:w-16 lg:h-16 lg:mb-8 rounded-xl lg:rounded-2xl bg-black border-2 border-neon-green flex items-center justify-center flex-shrink-0"
                  style={{
                    boxShadow: '0 0 30px rgba(0, 255, 65, 0.5), 0 0 60px rgba(0, 255, 65, 0.15)',
                  }}
                >
                  <i className="ri-map-pin-line text-xl lg:text-3xl text-neon-green"></i>
                </div>
                <div className="flex-1 lg:flex-none">
                  <h3 className="text-white text-lg lg:text-2xl font-bold mb-1 lg:mb-4">
                    地元の応援
                  </h3>
                  <div
                    className="w-2/5 h-0.5 bg-neon-green mb-3 lg:mb-6 hidden lg:block"
                    style={{
                      boxShadow: '0 0 10px rgba(0,255,65,0.8), 0 0 20px rgba(0,255,65,0.3)',
                    }}
                  />
                  <p className="text-gray-400 text-xs lg:text-base leading-relaxed">
                    あなたの街で活動するアーティストを直接サポート。体温のある音楽シーンを育てていく。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 3 */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-br from-neon-blue/10 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500" />
            <div className="relative bg-black/30 backdrop-blur-sm rounded-2xl p-6 lg:p-10 border border-white/5 hover:border-neon-blue/40 transition-all duration-500 hover:shadow-[0_0_40px_rgba(0,217,255,0.12)]">
              <div className="flex lg:block items-center gap-4">
                <div
                  className="w-12 h-12 lg:w-16 lg:h-16 lg:mb-8 rounded-xl lg:rounded-2xl bg-black border-2 border-neon-blue flex items-center justify-center flex-shrink-0"
                  style={{
                    boxShadow: '0 0 30px rgba(0, 217, 255, 0.5), 0 0 60px rgba(0, 217, 255, 0.15)',
                  }}
                >
                  <i className="ri-heart-pulse-line text-xl lg:text-3xl text-neon-blue"></i>
                </div>
                <div className="flex-1 lg:flex-none">
                  <h3 className="text-white text-lg lg:text-2xl font-bold mb-1 lg:mb-4">
                    摩擦を求めて
                  </h3>
                  <div
                    className="w-2/5 h-0.5 bg-neon-blue mb-3 lg:mb-6 hidden lg:block"
                    style={{
                      boxShadow: '0 0 10px rgba(0,217,255,0.8), 0 0 20px rgba(0,217,255,0.3)',
                    }}
                  />
                  <p className="text-gray-400 text-xs lg:text-base leading-relaxed">
                    完璧に磨かれた音楽ではなく、ざらついた、未完成な、でも心を揺さぶる音。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
